import { Button, Space, Switch, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "antd/es/table";
import { ICouponFamily } from "@interfaces";
import { memo, useEffect } from "react";
import dayjs from "dayjs";
import { dateFormat, enumStatus } from "@configs";
import {
  getApiErrorMessage,
  theme,
  useNotify,
  useResendCouponSms,
  useUpdateCouponStatus,
} from "@utils";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

interface IProps {
  data: ICouponFamily[];
  callback: () => void;
}
export const CouponFamilyTable = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();
  const updateCouponStatus = useUpdateCouponStatus();
  const resendCouponSms = useResendCouponSms();

  const isUpdating = updateCouponStatus.isPending || resendCouponSms.isPending;

  useEffect(() => {
    dispatch(setLoading(isUpdating));
  }, [dispatch, isUpdating]);

  const color = (status: enumStatus) => {
    switch (status) {
      case enumStatus.ACTIVE:
        return theme.colors.active;
        break;
      case enumStatus.INACTIVE:
        return theme.colors.expired;
        break;
      case enumStatus.REDEEMED:
        return theme.colors.active;
        break;
      case enumStatus.REJECT:
        return theme.colors.reject;
        break;
      default:
        return theme.colors.expired;
        break;
    }
  };
  const getStatus = (status: enumStatus) => {
    switch (status) {
      case enumStatus.ACTIVE:
        return t("page.active");
        break;
      case enumStatus.INACTIVE:
        return t("page.inactive");
        break;
      case enumStatus.REJECT:
        return t("page.rejected");
        break;
      case enumStatus.REDEEMED:
        return t("page.redeemed");
        break;
      case enumStatus.EXPIRED:
        return t("page.expired");
        break;
      default:
        return status;
        break;
    }
  };

  const dataSource: ICouponFamily[] = data;

  const columns: ColumnsType<ICouponFamily> = [
    {
      title: t("page.coupon_code"),
      dataIndex: "couponCode",
      key: "1",
    },
    {
      title: t("page.program_name"),
      dataIndex: "programName",
      render: (item, record) => <>{record?.program?.programName || t("page.empty")}</>,
      key: "2",
      sorter: (a, b) => {
        if (a?.program?.programName && b?.program?.programName) {
          return a?.program?.programName.localeCompare(b?.program?.programName);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.coupon_family"),
      dataIndex: "familyName",
      render: (item, record) => <>{record?.couponFamily?.familyName || t("page.empty")}</>,
      key: "3",
      sorter: (a, b) => {
        if (a?.couponFamily?.familyName && b?.couponFamily?.familyName) {
          return a?.couponFamily?.familyName.localeCompare(b?.couponFamily?.familyName);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.service_category"),
      dataIndex: "serviceCategory",
      key: "4",
    },
    {
      title: t("page.cr_date"),
      dataIndex: "redemeedDate",
      key: "5",
      render: (data) => (
        <span>{data !== "-" ? dayjs(data).format("DD-MM-YYYY HH:mm") : t("page.empty")}</span>
      ),
      // sorter: (a, b) => {
      //   if (dayjs(a.redemeedDate).isAfter(dayjs(b.redemeedDate))) {
      //     return dayjs(a.redemeedDate).isAfter(b.redemeedDate)
      //   } else {
      //     return 1;
      //   }
      // },
    },
    {
      title: t("page.valid_from"),
      dataIndex: "validFrom",
      key: "6",
      render: (date) => <span>{dayjs(date).format(dateFormat) || t("page.empty")}</span>,
    },
    {
      title: t("page.valid_to"),
      dataIndex: "validTo",
      key: "7",
      render: (date) => <span>{dayjs(date).format(dateFormat) || t("page.empty")}</span>,
    },
    {
      title: t("page.coupon_status"),
      dataIndex: "status",
      key: "8",
      render: (status) => (
        <Tag color={color(status)} key={status}>
          {getStatus(status)}
        </Tag>
      ),
    },
    {
      title: t("page.client_name"),
      dataIndex: "client",
      key: "9",
      render: (client) => <span key={client?._id}>{client?.name || t("page.empty")}</span>,
      sorter: (a, b) => {
        if (a.client?.name && b.client?.name) {
          return a.client?.name.localeCompare(b.client?.name);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.client_phone"),
      dataIndex: "client",
      key: "10",
      render: (client) => <span key={client?._id}>{client?.phoneNumber || t("page.empty")}</span>,
    },
    {
      title: t("page.provider_name"),
      dataIndex: "doctor",
      key: "11",
      render: (doctor) => <span key={doctor?._id}>{doctor?.name || t("page.empty")}</span>,
    },
    {
      title: t("page.actions"),
      key: "12",
      render: (_, record) => (
        <Space size="middle">
          <Button disabled={record?.status !== enumStatus.ACTIVE} type="primary" size={"small"} onClick={()=>handleSendSms(record)}>
            {t("page.resend_sms")}
          </Button>
          <Switch
            checked={record.status === enumStatus.ACTIVE}
            onChange={(value: boolean) => onChange(value, record)}
            disabled={enumStatus.REJECT === record.status || enumStatus.REDEEMED === record.status || enumStatus.CANCEL === record.status || enumStatus.EXPIRED === record.status}
          />
        </Space>
      ),
    },
  ];
  const onChange = async (value: boolean, record: ICouponFamily) => {
    const updateStatus = value ? enumStatus.ACTIVE : enumStatus.INACTIVE;
    try {
      await updateCouponStatus.mutateAsync({
        payload: { status: updateStatus } as ICouponFamily,
        id: record.couponId || "",
      });
      await callback();
      success(t("message.update.success"));
    } catch (err) {
      error(getApiErrorMessage(err, t("message.update.fail")));
    }
  };
  const handleSendSms = async (record: ICouponFamily) => {
    try {
      await resendCouponSms.mutateAsync({
        userId: record?.client?._id,
        couponCode: record?.couponCode,
      });
      success(t("message.send.resend_success"));
    } catch (err) {
      error(getApiErrorMessage(err, t("message.update.fail")));
    }
  };
  return (
    <Table dataSource={dataSource} rowKey={"couponFamilyId"} columns={columns} pagination={false} />
  );
});
